import { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Phone, Mail, ChevronRight, Sparkles, ShieldCheck,
  Zap, CheckCircle2, AlertTriangle, MapPin, TrendingUp, MessageCircle,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import { INDUSTRIES, INDUSTRY_FAQS, getIndustryBySlug } from "@/config/industries";
import PlugitAIChat from "@/pages/PlugitAIChat";
import KitTicker from "@/pages/KitTicker";

const C = BRAND.colors;

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

export default function IndustryPage() {
  const { industry: slug } = useParams();
  const ind = useMemo(() => getIndustryBySlug(slug), [slug]);

  useEffect(() => {
    if (!ind) return;
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    const url = `${origin}/for/${ind.slug}`;
    const title = `AI Website & Sales Agent for ${ind.display} | PlugitAI`;
    const desc = `${ind.tagline} PlugitAI builds AI-powered websites and sales agents for ${ind.audience} across DFW. Lock your project in for $299.99.`;
    document.title = title;
    upsertMeta("description", desc);
    upsertMeta("robots", "index,follow,max-image-preview:large,max-snippet:-1");
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", desc, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", `${origin}/hero-ai-twin.jpg`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertCanonical(url);

    upsertJsonLd("ld-industry-service", {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: `AI website and sales agent for ${ind.display}`,
      provider: {
        "@type": "Organization",
        name: "PlugitAI",
        url: origin,
        telephone: "+12148155966",
        email: "cgarciathanksyou@gmail.com",
      },
      areaServed: { "@type": "AdministrativeArea", name: "Dallas–Fort Worth, TX" },
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: "299.99",
        description: "$299.99 project lock-in. Full builds typically under $1,800.",
      },
      description: desc,
    });
    upsertJsonLd("ld-industry-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Industries", item: `${origin}/for` },
        { "@type": "ListItem", position: 3, name: ind.display, item: url },
      ],
    });
    upsertJsonLd("ld-industry-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: INDUSTRY_FAQS(ind).map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    return () => {
      ["ld-industry-service", "ld-industry-breadcrumb", "ld-industry-faq"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [ind]);

  if (!ind) return <Navigate to="/for" replace />;

  const faqs = INDUSTRY_FAQS(ind);
  const accent = ind.accent;
  const accentSoft = ind.accentSoft;

  return (
    <div className="min-h-screen w-full overflow-x-hidden"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid={`industry-page-${ind.slug}`}>
      <TopNav />
      <KitTicker />

      {/* Ambient bg tuned to the industry accent */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{
             background: `radial-gradient(1200px 700px at 15% -10%, ${accent}22, transparent 60%),
                          radial-gradient(900px 600px at 90% 20%, ${accent}18, transparent 60%),
                          radial-gradient(1400px 900px at 60% 110%, ${C.blueDeep}44, transparent 70%)`,
           }} />

      {/* Hero */}
      <section className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <Breadcrumbs ind={ind} />

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em] mt-6 mb-8"
               style={{
                 background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                 color: "white",
                 boxShadow: `0 12px 32px -10px ${accent}88`,
               }}>
            <Sparkles size={12} />
            Built for {ind.display}
          </div>

          <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 5.4rem)", color: "white" }}
              data-testid="industry-h1">
            AI websites & sales agents for{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: `drop-shadow(0 0 24px ${accent}66)`,
            }}>
              {ind.display}
            </span>
            .
          </h1>

          <p className="mt-6 max-w-[760px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
            {ind.tagline}
          </p>
          <p className="mt-4 max-w-[760px] text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>
            {ind.hook}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-7 h-14 rounded-full font-semibold text-base transition-all hover:-translate-y-0.5"
               style={{
                 background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                 color: "white",
                 boxShadow: `0 24px 60px -20px ${accent}aa`,
               }}
               data-testid="industry-cta-call">
              <Phone size={14} /> Talk to Kit — (214) 815-5966 <ArrowRight size={16} />
            </a>
            <button type="button"
                    onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                    className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                    style={{ borderColor: `${accent}77`, color: "white" }}
                    data-testid="industry-cta-chat">
              <MessageCircle size={14} /> Chat with the AI now <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="relative z-10 py-20 md:py-28 border-y"
               style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
               data-testid="industry-pains">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            The 4 leaks in {ind.name.toLowerCase()}
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-12"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
            Where your competitors are quietly losing money.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {ind.pains.map((p, i) => (
              <div key={i}
                   className="rounded-2xl p-5 md:p-6 border"
                   style={{
                     borderColor: `${accent}33`,
                     background: "rgba(17,24,39,0.55)",
                     backdropFilter: "blur(10px)",
                   }}
                   data-testid={`industry-pain-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                       style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
                    <AlertTriangle size={18} style={{ color: accentSoft }} />
                  </div>
                  <div>
                    <h3 className="font-black text-white text-lg mb-1.5">{p.title}</h3>
                    <p className="text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>
                      {p.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the AI twin does */}
      <section className="relative z-10 py-24 md:py-32" data-testid="industry-twin-does">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            What your {ind.name.toLowerCase()} AI twin does every day
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-12"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
            8 jobs. Zero payroll.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {ind.twinDoes.map((line, i) => (
              <div key={i}
                   className="rounded-2xl p-4 md:p-5 border flex items-start gap-3"
                   style={{
                     borderColor: C.line,
                     background: "rgba(17,24,39,0.55)",
                     backdropFilter: "blur(10px)",
                   }}
                   data-testid={`industry-twin-does-${i}`}>
                <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: accent }} />
                <div className="text-sm md:text-base font-semibold text-white leading-snug">{line}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI math */}
      <ROISection ind={ind} />

      {/* Guardrails */}
      <section className="relative z-10 py-20 md:py-28 border-y"
               style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
               data-testid="industry-guardrails">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3 flex items-center gap-2"
               style={{ color: accentSoft }}>
            <ShieldCheck size={12} /> Approved-only rails
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-6"
              style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", color: "white" }}>
            The AI never freelances.
          </h2>
          <p className="max-w-[680px] text-base md:text-lg mb-10 leading-relaxed" style={{ color: C.textDim }}>
            Your {ind.name.toLowerCase()} AI twin only says what you approve during setup. Every promise, price, and process runs on rails you defined.
          </p>
          <ul className="space-y-3">
            {ind.guardrails.map((g, i) => (
              <li key={i}
                  className="rounded-xl p-4 border flex items-start gap-3"
                  style={{ borderColor: `${accent}33`, background: "rgba(17,24,39,0.55)" }}
                  data-testid={`industry-guardrail-${i}`}>
                <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: accent }} />
                <span className="text-sm md:text-base text-white/90 leading-snug">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Proof / mini case study */}
      <section className="relative z-10 py-24 md:py-32" data-testid="industry-proof">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="relative rounded-3xl p-8 md:p-14 border overflow-hidden"
               style={{
                 background: `linear-gradient(135deg, ${accent}18 0%, ${accentSoft}10 100%), ${C.inkSoft}`,
                 borderColor: `${accent}55`,
                 boxShadow: `0 80px 200px -60px ${accent}55`,
               }}>
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
              Real result
            </div>
            <h3 className="font-black tracking-[-0.02em] leading-[1.02] mb-6"
                style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)", color: "white" }}>
              {ind.proof.headline}
            </h3>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>
              {ind.proof.body}
            </p>
          </div>
        </div>
      </section>

      {/* Services we cover */}
      <section className="relative z-10 py-16 md:py-24 border-t" style={{ borderColor: C.line }}>
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            We build for
          </div>
          <div className="flex flex-wrap gap-2">
            {ind.serves.map((s) => (
              <span key={s}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                    style={{
                      background: `${accent}12`,
                      borderColor: `${accent}55`,
                      color: accentSoft,
                    }}
                    data-testid={`industry-serves-${s.replace(/\s+/g, "-")}`}>
                <Sparkles size={12} /> {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* City cross-links — go to Industry × City combo pages */}
      <section className="relative z-10 py-16 md:py-20" data-testid="industry-cities">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            {ind.display} we serve across DFW
          </div>
          <div className="flex flex-wrap gap-2">
            {ind.topCities.map((slug) => (
              <Link key={slug} to={`/for/${ind.slug}/${slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border hover:-translate-y-0.5 transition-all"
                    style={{
                      background: "rgba(17,24,39,0.55)",
                      borderColor: C.line,
                      color: "white",
                    }}
                    data-testid={`industry-city-${slug}`}>
                <MapPin size={12} style={{ color: accentSoft }} />
                {slug.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")}, TX
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-24 md:py-32 border-t" style={{ borderColor: C.line }} data-testid="industry-faq">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            Questions from {ind.display}
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-10"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
            FAQ
          </h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i}
                       className="group rounded-2xl p-5 md:p-6 border"
                       style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)", backdropFilter: "blur(10px)" }}
                       data-testid={`industry-faq-${i}`}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer text-base md:text-lg font-bold text-white list-none">
                  <span>{f.q}</span>
                  <ChevronRight size={18} className="shrink-0 transition-transform group-open:rotate-90" style={{ color: accentSoft }} />
                </summary>
                <p className="mt-3 text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 pb-24 md:pb-36">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="relative rounded-3xl p-8 md:p-14 border overflow-hidden"
               style={{
                 background: `linear-gradient(135deg, ${accent}22 0%, ${accentSoft}12 100%), ${C.inkSoft}`,
                 borderColor: `${accent}66`,
                 boxShadow: `0 80px 200px -60px ${accent}66`,
               }}
               data-testid="industry-final-cta">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
              Lock in your build
            </div>
            <h3 className="font-black tracking-[-0.02em] leading-[1.02]"
                style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)", color: "white" }}>
              Ready to give your {ind.name.toLowerCase()} an AI twin?
            </h3>
            <p className="mt-4 max-w-[640px] text-base md:text-lg leading-relaxed text-white/85">
              Lock your project in with{" "}
              <span className="font-black" style={{ color: accentSoft }}>$299.99</span>{" "}
              and Kit will personally design the website, the AI twin, and the sales system around your {ind.name.toLowerCase()}. Not a template. Real work by real people.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="tel:+12148155966"
                 className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base"
                 style={{
                   background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                   color: "white",
                   boxShadow: `0 24px 60px -20px ${accent}aa`,
                 }}>
                <Phone size={14} /> Call (214) 815-5966 <ArrowRight size={16} />
              </a>
              <a href={`mailto:cgarciathanksyou@gmail.com?subject=${encodeURIComponent(`AI build for ${ind.display}`)}`}
                 className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border"
                 style={{ borderColor: `${accent}66`, color: "white" }}>
                <Mail size={14} /> Email me
              </a>
              <Link to="/for"
                    className="inline-flex items-center gap-2 px-4 h-11 rounded-full font-semibold text-sm border"
                    style={{ borderColor: C.line, color: C.textDim }}>
                See all industries <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <IndustryFooter ind={ind} />
      <PlugitAIChat />
    </div>
  );
}

// ─── ROI section — vertical-aware ───
function ROISection({ ind }) {
  const accent = ind.accent;
  const accentSoft = ind.accentSoft;
  const isAttorney = ind.slug === "attorneys";
  const isSalon = ind.slug === "salons";

  // Format $ figures
  const fmt = (n) => `$${(n ?? 0).toLocaleString()}`;

  // Data-driven ROI display — works for any industry schema (weeklyLoss / weeklyLift / dailyLoss)
  const roi = ind.roi || {};
  const isLift = roi.weeklyLift != null;                 // salons frame it as upside, not loss
  const roiValue = roi.weeklyLift ?? roi.weeklyLoss ?? roi.dailyLoss ?? 0;
  const roiCadence = roi.dailyLoss != null && roi.weeklyLoss == null && roi.weeklyLift == null
    ? "/ day"
    : isLift ? "/ week potential" : "/ week";
  const perDay = roi.missPerDay != null;
  const countValue = roi.missPerDay ?? roi.missPerWeek ?? roi.emptyPerWeek;
  const countLabel = roi.emptyPerWeek != null ? "empty slots" : "missed leads";
  const countCadence = perDay ? "per day" : "per week";
  const rateValue = roi.convertRate ?? roi.fillRate;
  const rateLabel = roi.fillRate != null ? "fillable with warm outreach" : "would-have-converted";
  const avgLabel = roi.avgCase != null ? "case value"
                 : roi.avgLTV != null ? "lifetime patient value"
                 : roi.avgJob != null ? "project value"
                 : "ticket";
  const avgValue = roi.avgCase ?? roi.avgLTV ?? roi.avgJob ?? roi.avgTicket;

  return (
    <section className="relative z-10 py-24 md:py-32 border-t"
             style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
             data-testid="industry-roi">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
          The math nobody wants to do
        </div>
        <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-4"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
          {isSalon ? "Every empty chair is money that doesn't come back." :
           isAttorney ? "Every unanswered ring is a case walking away." :
           ind.slug === "roofers" ? "Every unanswered storm-day call is a $12,000 job walking away." :
           ind.slug === "medical-clinics" ? "Every after-hours voicemail is a lifetime patient lost." :
           ind.slug === "contractors" ? "Every ghosted estimate is a signed project going next door." :
           ind.slug === "restaurants" ? "Every unanswered reservation is a seat filled somewhere else." :
           "Every missed call is a ticket walking down the street."}
        </h2>
        <p className="max-w-[680px] text-base md:text-lg mb-12 leading-relaxed" style={{ color: C.textDim }}>
          {roi.note}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {/* Left card — the loss */}
          <div className="rounded-2xl p-6 border md:col-span-2 relative overflow-hidden"
               style={{
                 borderColor: `${accent}55`,
                 background: `linear-gradient(135deg, ${accent}12 0%, transparent 100%), rgba(17,24,39,0.55)`,
                 boxShadow: `0 40px 100px -40px ${accent}55`,
               }}>
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
              {isLift ? "Weekly upside" : "What you're leaving on the table"}
            </div>
            <div className="flex items-baseline gap-3">
              <div className="font-black" style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)", color: accent, lineHeight: 1 }}>
                {fmt(roiValue)}
              </div>
              <div className="text-sm md:text-base font-bold" style={{ color: C.textDim }}>
                {roiCadence}
              </div>
            </div>
            <div className="mt-5 space-y-1.5 text-sm md:text-base" style={{ color: C.textDim }}>
              {countValue != null && (
                <div>{countValue} {countLabel} {countCadence} ×</div>
              )}
              {rateValue != null && (
                <div>{Math.round(rateValue * 100)}% {rateLabel} ×</div>
              )}
              {avgValue != null && (
                <div>${avgValue.toLocaleString()} average {avgLabel}</div>
              )}
            </div>
          </div>

          {/* Right card — the offer */}
          <div className="rounded-2xl p-6 border"
               style={{
                 borderColor: `${accent}33`,
                 background: "rgba(17,24,39,0.55)",
                 backdropFilter: "blur(10px)",
               }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                 style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
              <TrendingUp size={18} style={{ color: accent }} />
            </div>
            <div className="font-black text-white text-lg md:text-xl mb-2 leading-tight">
              Lock in your build for {" "}
              <span style={{ color: accentSoft }}>$299.99</span>.
            </div>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>
              Full builds land under $1,800. Most {ind.name.toLowerCase()} recover the entire investment inside the first month of new leads.
            </p>
            <button type="button"
                    onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                    className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-full font-semibold text-sm w-full justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                      color: "white",
                      boxShadow: `0 20px 50px -20px ${accent}aa`,
                    }}>
              <Zap size={14} /> Show me my build
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Breadcrumbs({ ind }) {
  return (
    <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 flex-wrap" style={{ color: C.textFade }}>
      <Link to="/" className="hover:text-white transition-colors">Home</Link>
      <ChevronRight size={12} />
      <Link to="/for" className="hover:text-white transition-colors">Industries</Link>
      <ChevronRight size={12} />
      <span style={{ color: C.text }}>{ind.display}</span>
    </nav>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}

function IndustryFooter({ ind }) {
  return (
    <footer className="relative z-10 border-t" style={{ borderColor: C.line, background: "rgba(10,15,30,0.85)" }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-11 w-auto mb-4" />
          <p className="text-sm max-w-[420px]" style={{ color: C.textDim }}>
            {BRAND.platformName} builds AI-powered websites and sales agents for {ind.audience} across the Dallas–Fort Worth metroplex.{" "}
            <span className="gold-shine gold-shine-dark uppercase tracking-[0.22em] text-[10px]">Powered by {BRAND.engineName}</span>.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Other industries</div>
          <ul className="space-y-2 text-sm">
            {INDUSTRIES.filter((x) => x.slug !== ind.slug).map((x) => (
              <li key={x.slug}>
                <Link to={`/for/${x.slug}`} className="hover:text-white" style={{ color: C.textDim }}>
                  {x.display}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/for" className="hover:text-white font-semibold" style={{ color: C.text }}>
                See all industries →
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Contact</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2" style={{ color: C.textDim }}>
              <Phone size={12} /> <a href="tel:+12148155966" className="hover:text-white">(214) 815-5966</a>
            </li>
            <li className="flex items-center gap-2" style={{ color: C.textDim }}>
              <Mail size={12} /> <a href="mailto:cgarciathanksyou@gmail.com" className="hover:text-white">Email Me</a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
